import React, { useMemo, useState } from "react";
import { Box, Button, Drawer, IconButton } from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { formatCurrency, formatIsoDate, formatPercentage, formatHandlerCode } from "../../../utils/namesFormater";
import { formatInterestType, formatLinkageType, formatRepaymentFrequency, formatRepaymentType, formatProductType, formatLoanToPolicyRatio } from "../../../utils/enumFormatter";
import { colorProductType } from "../../../utils/colorEnumFormatter";
import { Product, LoanDetails } from "../../../interfaces/DTO/mislaka_xml_models";
import InsuranceProductDrawer from "../Insurance/InsuranceProductDrawer";
import { handlerCodeColumn, productTypeColumn, currencyColumn, percentageColumn, isoDateColumn } from "../../../shared/UIParts/columnsFormatter";
import { calculateRemainingMonths } from "../../../utils/dateCalculators";
import LoanCalculator from "../../Loan/LoanCalculator";
import CloseIcon from "@mui/icons-material/Close";
import CalculateIcon from "@mui/icons-material/Calculate";


interface Props {
  products: Product[];
}

const LoansTable: React.FC<Props> = ({ products }) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedLoan, setSelectedLoan] = useState<LoanDetails | null>(null);
  const [isLoanCalculatorOpen, setIsLoanCalculatorOpen] = useState(false);
  const [hasOpenedLoanCalculator, setHasOpenedLoanCalculator] = useState(false);

const rows = useMemo(() => {
  const loanKeyCount: Record<string, number> = {};
  const firstOccurrence = new Map<string, boolean>();

  // סופרים כמה פעמים כל מפתח מופיע
  products.forEach((product) => {
    product.policies_or_accounts.forEach((policy) => {
      (policy.loan_details || []).forEach((loan) => {
        const key = `${product.details.handler_code}-${loan.loan_serial_number}`;
        loanKeyCount[key] = (loanKeyCount[key] || 0) + 1;
      });
    });
  });

  // בניית השורות עם זיהוי כפילויות
  return products.flatMap((product, productIndex) =>
    product.policies_or_accounts.flatMap((policy, policyIndex) => {
      const updateDate = policy.update_date;

      return (policy.loan_details || []).map((loan, loanIndex) => {
        const key = `${product.details.handler_code}-${loan.loan_serial_number}`;

        // בדיקה אם זה המופע הראשון של המפתח
        const isFirstOccurrence = !firstOccurrence.has(key);

        if (isFirstOccurrence) {
          firstOccurrence.set(key, true);
        }

        // isDuplicate = יש יותר ממופע אחד וזה לא המופע הראשון
        const isDuplicate = loanKeyCount[key] > 1 && !isFirstOccurrence;

        return {
          id: `${productIndex}-${policyIndex}-${loanIndex}`,
          product_type: product.details.product_type,
          handler_code: product.details.handler_code,
          policy_number: policy.policy_number,
          loan_serial_number: loan.loan_serial_number,
          loan_amount: loan.loan_amount,
          loan_balance: loan.loan_balance,
          loan_start_date: loan.loan_start_date,
          loan_end_date: loan.loan_end_date,
          interest_rate: loan.interest_rate,
          interest_type: loan.interest_type,
          linkage_type: loan.linkage_type,
          repayment_type: loan.repayment_type,
          repayment_frequency: loan.repayment_frequency,
          periodic_repayment_amount: loan.periodic_repayment_amount,
          loan_to_policy_ratio: loan.loan_to_policy_ratio,
          loan_period_months: calculateRemainingMonths(updateDate, loan.loan_end_date || ""),
          original_loan_period_months: loan.loan_period_months,
          total_loan_months: calculateRemainingMonths(loan.loan_start_date, loan.loan_end_date || ""),
          total_to_repay:
            loan.periodic_repayment_amount && loan.loan_period_months
              ? loan.periodic_repayment_amount * loan.loan_period_months
              : null,
          product,
          loan, // האובייקט המקורי של ההלוואה
          updateDate, // תאריך עדכון
          isDuplicate,
        };
      });
    })
  );
}, [products]);


  const columns: GridColDef[] = [
    { field: "policy_number", headerName: "מספר פוליסה", width: 140 },
    handlerCodeColumn(),
    productTypeColumn(),
     {
      field: "actions",
      headerName: "פעולות",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalculateIcon sx={{ color: "#173829",ml:1.2 }} />}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLoan(params.row.loan);
            setHasOpenedLoanCalculator(true);
            setIsLoanCalculatorOpen(true);
          }}
        >
          הצג החזרים
        </Button>
      ),
    },
      { field: "loan_serial_number", headerName: "קוד הלוואה", width: 140 },
    currencyColumn("loan_amount", "סכום הלוואה", 130),
    currencyColumn("loan_balance", "יתרה לתשלום", 130),
    {
      field: "total_loan_months",
      headerName: "סה'כ חודשי הלוואה",
      width: 160,
  
    },
    {
      field: "original_loan_period_months",
      headerName: "תקופת הלוואה (חודשים)",
      width: 160,
    },
    {
      field: "loan_period_months", headerName: "חודשים לסיום",
      width: 160,
    },
    // currencyColumn("total_to_repay", "סה\"כ להחזר", 140),
     currencyColumn("periodic_repayment_amount", "החזר תקופתי", 130),
    isoDateColumn("loan_start_date", "מועד התחלה"),
    isoDateColumn("loan_end_date", "מועד סיום"),
    percentageColumn("interest_rate", "ריבית", 100),
    {
      field: "interest_type",
      headerName: "סוג ריבית",
      width: 120,
      renderCell: (params) => formatInterestType(params.value),
    },
    {
      field: "linkage_type",
      headerName: "סוג הצמדה",
      width: 120,
      renderCell: (params) => formatLinkageType(params.value),
    },
    {
      field: "repayment_type",
      headerName: "סוג החזר",
      width: 120,
      renderCell: (params) => formatRepaymentType(params.value),
    },
    {
      field: "repayment_frequency",
      headerName: "תדירות החזר",
      width: 120,
      renderCell: (params) => formatRepaymentFrequency(params.value),
    },
   
    { field: "loan_to_policy_ratio", headerName: "יחס הלוואה לפוליסה", width: 140, renderCell: (params) => formatLoanToPolicyRatio(params.value) },
   
  ];

  const handleRowClick = (params: GridRowParams) => {
    const match = products.find((product) =>
      product.policies_or_accounts.some(
        (p) => p.policy_number === params.row.policy_number
      )
    );
    if (match) {
      setSelectedProduct(match);
    }
  };

  const handleCloseDrawer = () => setSelectedProduct(null);

  return (
    <Box sx={{ width: "100%" }}>
      <StyledDataGrid
        rows={rows}
        columns={columns}
        onRowClick={handleRowClick}
        pageSizeOptions={[10, 25, 50]}
        autoHeight
        getRowClassName={(params) =>
          params.row.isDuplicate ? 'loan-duplicate-row' : ''
        }
         sx={{
          "& .loan-duplicate-row": {
            color: "gray",
            opacity: 0.6,
          },
        }}
      />
      {selectedProduct && (
        <InsuranceProductDrawer
          product={selectedProduct}
          onClose={handleCloseDrawer}
        />
      )}

      {hasOpenedLoanCalculator && (
        <Drawer
          anchor="bottom"
          open={isLoanCalculatorOpen}
          onClose={() => setIsLoanCalculatorOpen(false)}
          slotProps={{
            paper: {
              sx: {
                height: "90vh",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            },
          }}
        >
          <Box sx={{ position: "relative", height: "100%" }}>
            <IconButton
              onClick={() => setIsLoanCalculatorOpen(false)}
              sx={{
                position: "absolute",
                left: 16,
                top: 16,
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ height: "100%", overflowY: "auto" }}>
              {selectedLoan && (
                <LoanCalculator
                  loanDetails={selectedLoan}
                  autoCalculate={true}
                  reportedPeriod={selectedLoan && products.find(p =>
                    p.policies_or_accounts.some(pol =>
                      pol.loan_details?.some(l => l === selectedLoan)
                    )
                  )?.policies_or_accounts.find(pol =>
                    pol.loan_details?.some(l => l === selectedLoan)
                  )?.update_date}
                />
              )}
            </Box>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default LoansTable;
